import { css } from "@emotion/css";
import { useEffect, useState } from "react";
import type {
  SearchFieldProps,
  ValidationResult,
  SearchFieldRenderProps,
} from "react-aria-components";
import { SearchField } from "react-aria-components";
import { useDebounce } from "usehooks-ts";

import { Input } from "../../internal/Input";

interface Props extends SearchFieldProps {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder?: string;
  compact?: boolean;
  testID: string;
  className?: string | ((values: SearchFieldRenderProps) => string) | undefined;
  maxWidth?: string;
  debounce?: number;
  handleChange?: () => void;
}

export function Search(props: Props) {
  const {
    label,
    description,
    errorMessage,
    placeholder,
    compact = true,
    maxWidth = "416px",
    testID,
    value: propValue,
    debounce = 300,
    onChange,
    handleChange,
    ...searchFieldProps
  } = props;
  const [value, setValue] = useState(propValue || "");
  const searchFilterWithDebounce = useDebounce(value, debounce);
  const handleSearchChange = (value: string) => {
    setValue(value);
    handleChange && handleChange();
  };

  useEffect(() => {
    if (propValue !== searchFilterWithDebounce) {
      onChange?.(searchFilterWithDebounce);
    }
  }, [searchFilterWithDebounce, propValue, onChange]);

  return (
    <SearchField
      className={css({ maxWidth }, styles.field)}
      {...searchFieldProps}
      data-testid={testID}
      onChange={handleSearchChange}
      value={value}
    >
      <Input
        leftIcon="search"
        placeholder={placeholder}
        clearable={Boolean(value?.length)}
        compact={compact}
        data-testid={`${testID}-input`}
      />
    </SearchField>
  );
}

const styles = {
  field: css({
    display: "flex",
    width: "100%",
    minWidth: 80,
  }),
};
